import { 
  Vector3, 
  Color,
  BufferGeometry, 
  BufferAttribute, 
  ShaderMaterial, 
  Points, 
  NormalBlending,
  MathUtils,
  TextureLoader
} from 'three';

import maskGalaxyImg from '../../../assets/textures/galaxy/mask_milk.png';
import vertexNebulaOp from '../../../shaders/nebula/vertexOp.glsl';
import fragmentNebulaOp from '../../../shaders/nebula/fragmentOp.glsl';
import { smoothstep } from '../helpers.js';
import { animatePartsGalaxy } from '../utils/many-bodies.js';
import { loading } from '../loading/loading.js';
import {sceneManager } from '../script.js';
import {
  INCLIN_GALAXY,
  NEB_OPAQUE_MAX_SPEED,
  NEB_OPAQUE_MIN_SPEED,
  NEB_OPAQUE_MAX_OFFSET,
  NEB_OPAQUE_MIN_OFFSET,
  NEB_OPAQUE_MAX_SIZE,
  NEB_OPAQUE_MIN_SIZE,
  RADIUS_GALAXY,
  WAVE_SPEED
} from '../configEntities/galaxyConfig.js';
import { attrNebulas } from '../configEntities/groupsAttr.js';

export class OpaqueNebula {
  constructor(numPoints, centerElement) {
    this.numPoints = numPoints;
    this.centerElement = centerElement;
    this.galaxyNormal = new Vector3(0, Math.cos(-1.0507), Math.sin(-1.0507)).normalize();
    this.speeds = [];
    this.warpPhase = 0

    this.positions = [];
    this.colors = [];
    this.sizes = [];
    this.randValue = [];
    this.angles = [];

    this.maskTexture = loading.textureLoader.load(
      maskGalaxyImg,
      () => this._initGeometry()
    );
  }

  _initGeometry() {
    const mask = this.maskTexture.image;
    const mw = mask.width, mh = mask.height;
    const cvs = document.createElement('canvas');
    cvs.width = mw; cvs.height = mh;
    const ctx = cvs.getContext('2d');
    ctx.translate(mw / 2, mh / 2);
    ctx.rotate(Math.PI);
    ctx.drawImage(mask, -mw / 2, -mh / 2);
    const maskData = ctx.getImageData(0, 0, mw, mh).data;

    const getMaskBrightness = (x, z) => {
      const u = x / (RADIUS_GALAXY * 2) + 0.5;
      const v = z / (RADIUS_GALAXY * 2) + 0.5;
      const ix = Math.min(Math.max(Math.floor(u * mw), 0), mw - 1);
      const iy = Math.min(Math.max(Math.floor(v * mh), 0), mh - 1);
      const idx = (iy * mw + ix) * 4;
      const r = maskData[idx], g = maskData[idx + 1], b = maskData[idx + 2];
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    };

    for (let i = 0; i < this.numPoints; i++) {
      const u = Math.random();
      const r = Math.sqrt(u * RADIUS_GALAXY * RADIUS_GALAXY);
      const theta = Math.random() * Math.PI * 2;
      const x = r * Math.cos(theta);
      const z = r * Math.sin(theta);

      const dUV = r / RADIUS_GALAXY;
      const f1 = 1 - smoothstep(0, 0.3, dUV, 1.5);
      const f2 = 1 - smoothstep(0, 1, dUV, 5);
      const yBase = NEB_OPAQUE_MAX_OFFSET * f1 + NEB_OPAQUE_MIN_OFFSET * f2;
      const y = (Math.random() * 2 - 1) * yBase;

      const brightness = getMaskBrightness(x, z);
      const prob = 0.1 + 0.9 * brightness;
      if (Math.random() > prob) continue;

      this.positions.push(x, y, z);
      this.randValue.push(Math.random() * 100 );
      this.angles.push(Math.PI - theta);

      const size = NEB_OPAQUE_MIN_SIZE + brightness * (NEB_OPAQUE_MAX_SIZE - NEB_OPAQUE_MIN_SIZE);
      this.sizes.push(size);

      const { color } = colorAleat(attrNebulas);
      this.colors.push(color.r, color.g, color.b);
      this.speeds.push(
        Math.random() * (NEB_OPAQUE_MAX_SPEED - NEB_OPAQUE_MIN_SPEED) + NEB_OPAQUE_MIN_SPEED
      );
    }

    this.geometry = new BufferGeometry();
    this.geometry.setAttribute('position', new BufferAttribute(new Float32Array(this.positions), 3));
    this.geometry.setAttribute('sizes', new BufferAttribute(new Float32Array(this.sizes), 1));
    this.geometry.setAttribute('randomValue', new BufferAttribute(new Float32Array(this.randValue), 1));
    this.geometry.setAttribute('angle', new BufferAttribute(new Float32Array(this.angles), 1));
    this.geometry.setAttribute('color', new BufferAttribute(new Float32Array(this.colors), 3));

    this.uniforms = {
      opacity: { value: 1.0 },
      u_time: { value: 0.0 },
      u_darkStrength: { value: 0.85 },
      u_density: { value: 0.8 },
      u_thinness: { value: 0.8 },
      u_warpStrength: { value: 0.5 },
      u_warpScale: { value: 4.0 },
      cameraPos:   { value: sceneManager.camera.position },
      diskCenter:  { value: this.centerElement.mesh.position },
      diskNormal:  { value: this.galaxyNormal },

      minRadius:   { value: RADIUS_GALAXY * 0.01 },
      maxRadius:   { value: RADIUS_GALAXY * 2 },
      maxCamDist:  { value: RADIUS_GALAXY * 3 },

      minThick:    { value: NEB_OPAQUE_MAX_SIZE*0.05 },
      maxThick:    { value: NEB_OPAQUE_MAX_SIZE},
      falloffExp:  { value: 0.5 },
      falloffExpMajor:  { value: 1.0 }, 
      fadePlane: {value:RADIUS_GALAXY * 0.02},
      fadeThick: {value:NEB_OPAQUE_MAX_SIZE * 0.02}
    };

    this.material = new ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: vertexNebulaOp,
      fragmentShader: fragmentNebulaOp,
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: NormalBlending
    });
    this.points = new Points(this.geometry, this.material);
    this.points.rotation.x = INCLIN_GALAXY;
    sceneManager.scene.add(this.points);
    this.originalPositions = this.geometry.attributes.position.array.slice();
  }

  centerOrbit() {
    if (this.points && this.centerElement && this.centerElement.mesh) {
      this.points.position.copy(this.centerElement.mesh.position);
    }
  }

  animate(speed) {
    if (!this.points) return;

    this.warpPhase = (this.warpPhase + speed *  WAVE_SPEED) % (2 * Math.PI);
    this.centerOrbit();
    animatePartsGalaxy(
      this.points,
      speed,
      this.warpPhase,
      this.speeds,
      this.originalPositions
    );
    this.material.uniforms.opacity.value = this.calculateOpacity(
      sceneManager.camera,
      this.centerElement.mesh.position,
      this.galaxyNormal,
      0.55,
      0.45
    );

    this.material.uniforms.u_time.value += speed * 2e-9;

    this.material.uniforms.cameraPos.value.copy(sceneManager.camera.position);
    this.material.uniforms.diskCenter.value.copy(this.centerElement.mesh.position);

  }
  calculateOpacity(camera, center, normal, a, b) {
    const camDir = new Vector3().subVectors(camera.position, center).normalize();
    const dot = Math.abs(camDir.dot(normal));
    return MathUtils.clamp(a * (1 - dot) + b, 0, 1);
  }
}

export function colorAleat(attr) {
  const total = attr.percentage.reduce((a, b) => a + b, 0);
  const rnd = Math.random() * total;
  let sum = 0, idx = 0;
  for (let i = 0; i < attr.percentage.length; i++) {
    sum += attr.percentage[i];
    if (rnd <= sum) { idx = i; break; }
  }
  const col = attr.color[idx];
  const r = (col >> 16) & 255;
  const g = (col >> 8) & 255;
  const b = col & 255;
  return { color: new Color(r / 255, g / 255, b / 255) };
}
